import { ValueLabel } from "@features/ax/shared/ValueLabel";
import { CustomTooltip } from "@shared/components/CustomTooltip";
import { Badge } from "@shared/ui/badge";
import { Button } from "@shared/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@shared/ui/card";
import { KeyStatsCard } from "./KeyStatsCard";
import {
  CircleOff,
  Download,
  Plus,
  Printer,
  Redo2Icon,
  Send,
  Settings,
  User,
} from "lucide-react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Tabs } from "@shared/ui/tabs";
import { HoverRevealText } from "@shared/components/HoverRevealText";
import { useState } from "react";
import MeterConsumption from "./tabs/MeterConsumption";
import PaymentHistory from "./tabs/PaymentHistory";
import { ActivityLogTab } from "./tabs/ActivityLog";
import { CorrectionTab } from "./tabs/CorrectionTab";
import {
  useCurrency,
  useRemoteUtilityId,
} from "@shared/selectors/globalSelectors";
import { useBillDetailsFromCorrection, useSendCommunication, useTemplates } from "../hooks";
import {
  useBillDetail,
  useDownloadBillAction,
  usePrintBillAction,
  usePrintDeliverBillAction,
} from "../singleBillGenaration/hooks";
import { getStatusBadgeClass } from "@shared/utils/statusUtils";
import { formatTerritoryAddress } from "@shared/utils/address";
import ChargeBreakdown from "./tabs/ChargeBreakdown";
import { SendBillDialog } from "./SendBillDialog";
import { mapToLabelValue } from "@shared/utils/mapToLabelValue";
import { useToast } from "@shared/hooks/use-toast";

// ============= EXTRACTED COMPONENTS =============
const SectionBlock = ({ title, children }) => (
  <div className="space-y-2 p-2 border rounded-md">
    <h3 className="text-sm font-semibold tracking-wide">{title}</h3>
    {children}
  </div>
);

const GridBlock = ({ items }) => (
  <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
    {items.map((item) => (
      <ValueLabel
        key={item.label}
        label={item.label}
        value={item.value}
        tooltip={item.tip}
        description={item.description}
        layout="vertical"
        className=""
        isHoverable={item.label.toLowerCase().includes("email")}
      />
    ))}
  </div>
);

const QUICK_ACTIONS_CONFIG = [
  {
    label: "View Consumer Details",
    icon: User,
    action: "VIEW_CONSUMER",
    className: "",
  },
  {
    label: "Download Bill",
    icon: Download,
    action: "DOWNLOAD_BILL",
    className: "",
  },
  {
    label: "Print Bill",
    icon: Printer,
    action: "PRINT_BILL",
    className: "",
  },
  {
    label: "Send Bill",
    icon: Send,
    action: "SEND_BILL",
  },
  {
    label: "Print & Deliver Bill",
    icon: Printer,
    action: "PRINT_DELIVER",
  },
];

// ============= MAIN COMPONENT =============
function BillDetailView() {
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const params = useParams();
  const navigate = useNavigate();
  const currency = useCurrency();
  const {toast} = useToast()
  const billId = params?.id;
  console.log("debug bill id", billId);
  const [searchParams] = useSearchParams();
  const correctionId = searchParams.get("correction_id");
  const remoteUtilityId = useRemoteUtilityId();
  const { data: correctedData } = useBillDetailsFromCorrection(
    remoteUtilityId,
    correctionId
  );
  console.log("debug corrected", correctedData);
  const { mutate: printBill } = usePrintBillAction();
  const { mutate: downloadBill } = useDownloadBillAction();
  const { mutate: printAndDeliverBill } = usePrintDeliverBillAction();

  const { data: billDetail } = useBillDetail(billId);
  const sendCommunication = useSendCommunication(billDetail?.consumerAndServiceInfo?.accountNumber || "");
const { data: templates } = useTemplates({
    channelType: "EMAIL",
    remoteUtilityId,
  });
  const templateList = mapToLabelValue(templates, "templateName", "id");
  // ============= ACTION HANDLERS MAP =============
  const actionHandlers = {
    VEW_CONSUMER: () => navigate(`/cx/`),
    DOWNLOAD_BILL: () => downloadBill({ billId }, {
      onSuccess: () => toast({title: "Bill downloaded successfully."})
    }),
    PRINT_BILL: () => printBill(billId, {
      onSuccess: () => toast({title: "Bill queued for printing successfully."})
    }),
    SEND_BILL: () => setIsAddDialogOpen(true),
    PRINT_DELIVER: () => printAndDeliverBill(billId, {
      onSuccess: () => toast({title: "Bill queued for printing and distribution successfully."})
    }),
  };

  const handleQuickAction = (action) => {
    const handler = actionHandlers[action];
    if (handler) handler();
  };

  // ============= TAB CONFIGURATION =============
  const tabComponents = {
    ...(correctionId && {
      correction: {
        label: "Corrections",
        component: <CorrectionTab correctionId={correctionId} />,
      },
    }),
    "charge-breakdown": {
      label: "Charge Breakdown",
      component: (
        <ChargeBreakdown
          planInfo={billDetail?.planInfo}
          ratesInfo={billDetail?.ratesInfo}
        />
      ),
    },
    "meter-consumption": {
      label: "Meter & Consumption",
      component: (
        <MeterConsumption
          consumerNo={billDetail?.consumerAndServiceInfo?.accountNumber}
        />
      ),
    },
    "payment-history": {
      label: "Payment History",
      component: <PaymentHistory paymentHistory={billDetail?.paymentHistory} />,
    },
    "activity-log": {
      label: "Activity Logs",
      component: <ActivityLogTab />,
    },
  };

  const consumerDetails = billDetail?.consumerAndServiceInfo ?? {};
  const formatAddress = (address) => {
    const serviceAddress = { service: address };
    return formatTerritoryAddress(serviceAddress, "service");
  };
  // ============= SECTION DATA CONFIGURATIONS =============
  const getCycleDetailsItems = () => [
    {
      label: "Consumer Name",
      value: consumerDetails?.consumerName ?? "NA",
    },
    {
      label: "Account Number",
      value: consumerDetails?.accountNumber ?? "NA",
    },
    {
      label: "Email",
      value: consumerDetails?.email ?? "NA",
    },
    {
      label: "Phone",
      value: consumerDetails?.phone ?? "NA",
    },
    {
      label: "Service Type",
      value: consumerDetails?.serviceType ?? "NA",
    },
    {
      label: "Meter Number",
      value: consumerDetails?.meterNumber ?? "NA",
    },
    {
      label: "Tariff Plan",
      value: consumerDetails?.tariffPlan ?? "NA",
    },
    {
      label: "Bill Cycle",
      value: consumerDetails?.billCycle ?? "NA",
    },
    {
      label: "Service Address",
      value: formatAddress(consumerDetails?.serviceAddress) ?? "NA",
    },
  ];
  const auditInfo = billDetail?.auditInformation ?? {};
  const getLastRunDetailsItems = () => [
    {
      label: "Created By",
      value: auditInfo?.createdBy ?? "NA",
    },
    {
      label: "Created On",
      value: auditInfo?.createdOn ?? "NA",
    },
    {
      label: "Approved By",
      value: auditInfo?.approvedBy ?? "NA",
    },
    {
      label: "Approved On",
      value: auditInfo?.approvedOn ?? "NA",
    },
    {
      label: "Bill Period",
      value: `${auditInfo?.billPeriod?.from ?? "NA"} - ${
        auditInfo?.billPeriod?.to ?? "NA"
      } `,
    },
  ];
  const handleSendEmail = (templateId) => {
    const communicationData = {
        remoteUtilityId,
        channelType: 0, // for mail from reference in cx
        template_id: templateId,
        to: [billDetail?.consumerAndServiceInfo?.email],
        context: {
          entityId: billId,
          actionType: 'bill',
        },
      };

      // Send the communication using the hook
      sendCommunication.mutateAsync(communicationData);
  }
  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col lg:flex-row">
        {/* Left Column */}
        <div className="mr-4 w-full lg:w-[70%] space-y-6">
          <Card className="overflow-hidden">
            {/* ================= HEADER ================= */}
            <div className="">
              <CardHeader className="flex flex-row items-center justify-between border-none">
                <div className="flex flex-col items-start">
                  <div>
                    <CardTitle className="pl-2 pb-2">
                      {billDetail?.invoice?.invoiceNumber}
                    </CardTitle>
                  </div>
                  <span
                    className="
                  flex gap-1"
                  >
                    <Badge
                      className={getStatusBadgeClass(
                        billDetail?.invoice?.status?.generationStatus
                      )}
                    >
                      {billDetail?.invoice?.status?.generationStatus}
                    </Badge>
                    <Badge
                      className={getStatusBadgeClass(
                        billDetail?.invoice?.status?.paymentStatus
                      )}
                    >
                      {billDetail?.invoice?.status?.paymentStatus}
                    </Badge>
                  </span>
                </div>

                <div className="flex items-center gap-3">
                  <div className="flex border border-gray-200 rounded-lg overflow-hidden divide-x divide-gray-200">
                    <div className="px-4 py-3 bg-blue-50/50">
                      <div className="text-xs font-bold text-blue-600">
                        TOTAL BILL AMOUNT
                      </div>
                      <div className="text-xl font-bold text-blue-700 mt-1">
                        {currency} {billDetail?.invoice?.totalBillAmount}
                      </div>
                    </div>
                    <div className="px-4 py-3 bg-white">
                      <div className="text-xs text-gray-400">DUE DATE</div>
                      <div className="text-lg font-bold text-gray-700 mt-1">
                        {billDetail?.invoice?.dueDate}
                      </div>
                    </div>
                  </div>
                </div>
              </CardHeader>
            </div>

            {/* ================= BODY CONTENT ================= */}
            <CardContent className="pt-4 space-y-2 border-none">
              <SectionBlock title="Consumer & Service Info">
                <GridBlock items={getCycleDetailsItems()} />
              </SectionBlock>

              <SectionBlock title="Audit Information">
                <GridBlock items={getLastRunDetailsItems()} />
              </SectionBlock>

              {/* <SectionBlock title="Audit Information">
                <GridBlock items={getAuditInformationItems()} />
              </SectionBlock> */}
            </CardContent>
          </Card>
        </div>

        {/* Right Column */}
        <div className="lg:w-[30%] space-y-4">
          <KeyStatsCard
            data={{
              lastPaidAmount: billDetail?.paymentStatus?.lastPaidAmount,
              lastPaidOn: billDetail?.paymentStatus?.lastPaidOn,
              remaining: billDetail?.paymentStatus?.remainingPayable,
            }}
          />

          {/* Quick Actions */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-start gap-4">
              <CardTitle className="text-base">Quick Actions</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {QUICK_ACTIONS_CONFIG.map((action) => (
                <div
                  key={action.label}
                  className="flex items-center justify-between w-full"
                >
                  <Button
                    variant="outline"
                    className={`${action.className} w-full justify-center text-center disabled:cursor-not-allowed`}
                    onClick={() => handleQuickAction(action.action)}
                  >
                    <action.icon className="h-4 w-4 mr-2" />
                    {action.label}
                  </Button>
                </div>
              ))}
            </CardContent>
          </Card>
        </div>
      </div>

      <Tabs
        defaultValue={correctionId ? "correction" : "charge-breakdown"}
        tabComponents={tabComponents}
        tabsListClassName={`grid ${
          correctionId ? "grid-cols-5" : "grid-cols-4"
        } gap-2`}
        className="space-y-4"
      />
      <SendBillDialog
        open={isAddDialogOpen}
        onOpenChange={setIsAddDialogOpen}
        email={billDetail?.consumerAndServiceInfo?.email}
        templates={templateList}
        onSend={(templateId) => {
          handleSendEmail(templateId)
        }}
      />
    </div>
  );
}

export default BillDetailView;
